<script setup>
import { ref, watch } from "vue";
import { Inertia } from "@inertiajs/inertia";
import AppLayout from "@/Layouts/AppLayout.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import PaginationLinks from "@/Components/PaginationLinks.vue";
import ConfirmationModal from "@/Components/ConfirmationModal.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import _ from "lodash";
import SalesPlanIndexFilters from "../../../Components/Sales/SalesPlanIndexFilters.vue";

const props = defineProps({
  leads: Object,
  filters: Object,
  sorting: Object,
  projectManagers: Array,
});

const defaultSorting = { column: "created_at", is_ascending: false };

const defaultFilters = {
  client: null,
  contact_name: null,
  contact_surname: null,
  contact_email: null,
  contact_number: null,
  employeeID: null,
  date_due: null,
  successful_lead: null,
  action_plan: null,
  notes: null,
  start_date_from: null,
  start_date_to: null,
  date_due_from: null,
  date_due_to: null,
};

const searching = ref(false);

const loadPersisted = () => {
  try {
    const f = localStorage.getItem("salesplan.filters");
    const s = localStorage.getItem("salesplan.sorting");
    return {
      filters: f ? JSON.parse(f) : null,
      sorting: s ? JSON.parse(s) : null,
    };
  } catch (e) {
    return { filters: null, sorting: null };
  }
};

const persisted = loadPersisted();

const sorting = ref(
  Object.assign({}, defaultSorting, persisted.sorting || props.sorting || {})
);

const filterValues = ref({
  ...defaultFilters,
  ...(props.filters || {}),
  ...(persisted.filters || {}),
});

const compactFilters = (obj) => {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v !== null && v !== undefined && String(v).trim() !== "") out[k] = v;
  });
  return out;
};

const executeSearchRequest = () => {
  searching.value = true;

  const queryParameters = route().params || {};
  if (queryParameters.page) delete queryParameters.page;

  queryParameters.filters = compactFilters(filterValues.value);
  queryParameters.sorting = sorting.value;

  try {
    localStorage.setItem("salesplan.filters", JSON.stringify(queryParameters.filters));
    localStorage.setItem("salesplan.sorting", JSON.stringify(queryParameters.sorting));
  } catch (e) {}

  Inertia.get(route("sales.salesplan.index", queryParameters), {}, {
    preserveScroll: true,
    preserveState: true,
    replace: true,
    onFinish: () => {
      searching.value = false;
    },
  });
};

const makeSearchRequest = _.debounce(() => executeSearchRequest(), 400);

const resetFilters = () => {
  searching.value = true;
  filterValues.value = { ...defaultFilters };
  executeSearchRequest();
};

watch(sorting, () => executeSearchRequest(), { deep: true });

const sortTable = (column) => {
  if (column === sorting.value.column) {
    sorting.value.is_ascending = !sorting.value.is_ascending;
  } else {
    sorting.value = { column, is_ascending: true };
  }
};

const sortingClass = (column) => {
  if (column !== sorting.value.column) return ["fa", "fa-sort"];
  return sorting.value.is_ascending ? ["fa", "fa-sort-down"] : ["fa", "fa-sort-up"];
};

// -------------------- Delete modal --------------------
const showDeleteModal = ref(false);
const leadToDelete = ref(null);

const confirmDelete = (lead) => {
  leadToDelete.value = lead;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  leadToDelete.value = null;
};

const deleteLead = () => {
  if (!leadToDelete.value) return;

  Inertia.delete(route("sales.salesplan.destroy", leadToDelete.value.id), {
    preserveScroll: true,
    preserveState: true,
    onSuccess: () => {
      closeDeleteModal();
      Inertia.reload({
        only: ["leads"],
        preserveScroll: true,
        preserveState: true,
      });
    },
    onFinish: () => {
      // no-op
    },
  });
};

// -------------------- Mark Successful modal --------------------
const showSuccessfulModal = ref(false);
const leadToMarkSuccessful = ref(null);
const markingSuccessful = ref(false);

const confirmMarkSuccessful = (lead) => {
  leadToMarkSuccessful.value = lead;
  showSuccessfulModal.value = true;
};

const closeSuccessfulModal = () => {
  showSuccessfulModal.value = false;
  leadToMarkSuccessful.value = null;
  markingSuccessful.value = false;
};

const markSuccessful = () => {
  if (!leadToMarkSuccessful.value) return;

  markingSuccessful.value = true;

  // ✅ IMPORTANT: la ruta requiere {salesLead}
  Inertia.patch(
    route("sales.salesplan.successful", { salesLead: leadToMarkSuccessful.value.id }),
    {},
    {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        closeSuccessfulModal();
        Inertia.reload({
          only: ["leads"],
          preserveScroll: true,
          preserveState: true,
        });
      },
      onFinish: () => {
        markingSuccessful.value = false;
      },
    }
  );
};

// -------------------- Helpers --------------------
const safe = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const toDateString = (ts) => {
  if (!ts) return "—";
  const d = new Date(Number(ts) * 1000);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
};

const buildCompanyPrefill = (lead) => {
  const contact = [lead?.contact_name, lead?.contact_surname].filter(Boolean).join(" ").trim();

  const notes = [
    lead?.notes ? `Notes: ${lead.notes}` : null,
    lead?.action_plan ? `Action plan: ${lead.action_plan}` : null,
  ]
    .filter(Boolean)
    .join("\n\n");

  return {
    client: lead?.client ?? null,
    contact: contact || null,
    phone: lead?.contact_number ?? null,
    email: lead?.contact_email ?? null,
    notes: notes || null,
    sales_lead_id: lead?.id ?? null,
    accountmanager: lead?.employeeID ?? null,
  };
};
</script>

<template>
  <AppLayout title="Sales Plan">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">Sales Plan</h2>
    </template>

    <div class="mx-auto py-10 sm:px-6 lg:px-8">
      <div class="mb-4 space-y-4">
        <div class="flex flex-wrap gap-2">
          <a :href="route('sales.salesplan.create')">
            <PrimaryButton>Add Sales Lead</PrimaryButton>
          </a>
        </div>

        <!-- Filters -->
        <div class="grid gap-2 lg:grid-cols-4 md:grid-cols-2 grid-cols-1 w-full">
          <SalesPlanIndexFilters
            :filterValues="filterValues"
            :sorting="sorting"
            :projectManagers="projectManagers"
            :searching="searching"
            @search="makeSearchRequest"
            @reset="resetFilters"
          />
        </div>
      </div>

      <!-- Table -->
      <table class="min-w-full w-full text-left text-sm">
        <thead class="border-b">
          <tr>
            <th class="px-4 py-2">Thumbnail</th>

            <th class="px-4 py-2 cursor-pointer" @click="sortTable('start_date')">
              Start Date <span :class="sortingClass('start_date')"></span>
            </th>

            <th class="px-4 py-2 cursor-pointer" @click="sortTable('client')">
              Client <span :class="sortingClass('client')"></span>
            </th>

            <th class="px-4 py-2 cursor-pointer" @click="sortTable('contact_name')">
              Contact <span :class="sortingClass('contact_name')"></span>
            </th>

            <th class="px-4 py-2 cursor-pointer" @click="sortTable('contact_email')">
              Contact Email <span :class="sortingClass('contact_email')"></span>
            </th>

            <th class="px-4 py-2 cursor-pointer" @click="sortTable('contact_number')">
              Contact Phone <span :class="sortingClass('contact_number')"></span>
            </th>

            <th class="px-4 py-2 cursor-pointer" @click="sortTable('date_due')">
              Due Date <span :class="sortingClass('date_due')"></span>
            </th>

            <th class="px-4 py-2">Action Plan</th>
            <th class="px-4 py-2">Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="l in leads.data" :key="l.id" class="border-b align-top">
            <td class="px-4 py-2">
              <img v-if="l.url" :src="l.url" class="h-12 w-12 object-cover rounded" :alt="l.thumbnail" />
              <span v-else class="text-gray-400">—</span>
            </td>

            <td class="px-4 py-2">
              {{ toDateString(l.start_date) }}
            </td>

            <td class="px-4 py-2">
              <div class="font-medium">{{ safe(l.client) }}</div>
              <div class="text-xs text-gray-500" v-if="l.successful_lead">
                Closed deal
                <span v-if="l.date_successful_lead">({{ toDateString(l.date_successful_lead) }})</span>
              </div>
            </td>

            <td class="px-4 py-2">
              {{ safe(l.contact_name) }} {{ safe(l.contact_surname) }}
            </td>

            <td class="px-4 py-2">
              {{ safe(l.contact_email) }}
            </td>

            <td class="px-4 py-2">
              {{ safe(l.contact_number) }}
            </td>

            <td class="px-4 py-2">
              {{ toDateString(l.date_due) }}
            </td>

            <td class="px-4 py-2">
              <div class="whitespace-pre-line text-gray-700 line-clamp-3">
                {{ safe(l.action_plan) }}
              </div>
            </td>

            <td class="px-4 py-2 space-x-3">
              <a :href="route('sales.salesplan.show', l.id)" class="fa fa-eye hover:text-gray-900" title="View" />
              <a :href="route('sales.salesplan.edit', l.id)" class="fa fa-pen" title="Edit" />

              <button
                v-if="!l.successful_lead"
                type="button"
                class="fa fa-check hover:text-green-700"
                title="Mark as closed deal"
                @click="confirmMarkSuccessful(l)"
                :disabled="markingSuccessful"
              />

              <a
                :href="route('admin.companies.create', { prefill: buildCompanyPrefill(l) })"
                class="fa fa-plus hover:text-gray-900"
                title="Create company from lead"
              />

              <button
                type="button"
                class="fa fa-trash hover:text-red-800"
                title="Delete"
                @click="confirmDelete(l)"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div class="mt-4">
        <PaginationLinks :pagination="leads" />
      </div>
    </div>

    <!-- Delete modal -->
    <ConfirmationModal :show="showDeleteModal" @close="closeDeleteModal">
      <template #title>Delete sales lead</template>

      <template #content>
        <div class="text-sm text-gray-700">
          Are you sure you want to delete
          <span class="font-semibold">{{ leadToDelete?.client || "this lead" }}</span>?
        </div>
      </template>

      <template #footer>
        <SecondaryButton @click="closeDeleteModal">Cancel</SecondaryButton>

        <button
          type="button"
          class="ml-3 inline-flex items-center px-4 py-2 bg-red-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-red-700"
          @click="deleteLead"
        >
          Delete
        </button>
      </template>
    </ConfirmationModal>

    <!-- Mark successful modal -->
    <ConfirmationModal :show="showSuccessfulModal" @close="closeSuccessfulModal">
      <template #title>Mark as closed deal</template>

      <template #content>
        <div class="text-sm text-gray-700">
          <div class="font-semibold">Congratulations!</div>
          <div class="mt-1">
            Are you sure you want to mark
            <span class="font-semibold">{{ leadToMarkSuccessful?.client || "this lead" }}</span>
            as a closed deal?
          </div>
          <div class="mt-2 text-xs text-gray-500">
            This will set the closed deal date to today.
          </div>
        </div>
      </template>

      <template #footer>
        <SecondaryButton @click="closeSuccessfulModal" :disabled="markingSuccessful">
          Cancel
        </SecondaryButton>

        <button
          type="button"
          class="ml-3 inline-flex items-center px-4 py-2 bg-green-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-green-700 disabled:opacity-50"
          @click="markSuccessful"
          :disabled="markingSuccessful"
        >
          Confirm
        </button>
      </template>
    </ConfirmationModal>
  </AppLayout>
</template>

<style>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>